import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useMe } from "../../auth/queries";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { ErrorText, Panel } from "../../components/ui/feedback";
import { LabelGrid } from "../../components/ui/LabelGrid";
import { PageHeader } from "../../components/ui/PageHeader";
import { useI18n } from "../../i18n/I18nProvider";
import { apiRequest } from "../../lib/api";
import type { Invite, Member, Role } from "../../lib/api-types";
import { paths } from "../../lib/endpoints";
import { useWorkspace } from "../workspace/queries";
import { InviteForm, type CreatedInvite } from "./InviteForm";
import { InviteReveal } from "./InviteReveal";
import { MemberRow } from "./MemberRow";
import { PendingInviteRow } from "./PendingInviteRow";
import { inviteLinkFromApiUrl, invitesQuery, membersQuery } from "./queries";

export interface MembersPageProps {
  workspaceId: string;
}

/**
 * Members as label-grid rows, the invite form (owners only; others get a read-only note), and the
 * pending invitations. Resend issues a fresh link and retires the old one; Revoke asks first.
 */
export function MembersPage({ workspaceId }: MembersPageProps) {
  const { t } = useI18n();
  const me = useMe();
  const workspace = useWorkspace(workspaceId);
  // Members and invitations are owner-only on the API, so a member never requests them.
  const isMember = workspace.data?.role === "member";
  const members = useQuery({ ...membersQuery(workspaceId), enabled: !isMember });
  const invites = useQuery({ ...invitesQuery(workspaceId), enabled: !isMember });
  const queryClient = useQueryClient();
  const [removing, setRemoving] = useState<Member | null>(null);
  const [revoking, setRevoking] = useState<Invite | null>(null);
  const [shownLink, setShownLink] = useState<string | null>(null);

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["members", workspaceId] }),
      queryClient.invalidateQueries({ queryKey: ["invites", workspaceId] }),
    ]).then(() => undefined);

  const changeRole = useMutation({
    mutationFn: ({ operatorId, role }: { operatorId: string; role: Role }) =>
      apiRequest<{ operatorId: string; role: Role }>(paths.member(workspaceId, operatorId), {
        method: "PATCH",
        body: { role },
      }),
    onSuccess: refresh,
  });

  const removeMember = useMutation({
    mutationFn: (operatorId: string) =>
      apiRequest<{ removed: boolean }>(paths.member(workspaceId, operatorId), { method: "DELETE" }),
    onSuccess: () => {
      setRemoving(null);
      return refresh();
    },
  });

  const revokeInvite = useMutation({
    mutationFn: (inviteId: string) =>
      apiRequest<unknown>(paths.invite(workspaceId, inviteId), { method: "DELETE" }),
    onSuccess: () => {
      setRevoking(null);
      return refresh();
    },
  });

  const resend = useMutation({
    mutationFn: async (invite: Invite) => {
      const created = await apiRequest<CreatedInvite>(paths.invites(workspaceId), {
        method: "POST",
        body: { email: invite.email, role: invite.role },
      });
      await apiRequest<unknown>(paths.invite(workspaceId, invite.id), { method: "DELETE" });
      return inviteLinkFromApiUrl(created.url, window.location.origin);
    },
    onSuccess: (link) => {
      setShownLink(link);
      return refresh();
    },
  });

  const ownerId = members.data?.find((member) => member.role === "owner")?.operatorId;
  const pending: readonly Invite[] = invites.data ?? [];
  const busy =
    changeRole.isPending || removeMember.isPending || revokeInvite.isPending || resend.isPending;

  return (
    <div className="space-y-8">
      <PageHeader title={t("members.title")} />
      <ErrorText
        error={
          members.error ?? invites.error ?? changeRole.error ?? revokeInvite.error ?? resend.error
        }
      />

      {isMember ? (
        <Panel>
          <p className="text-operate text-ink-2">{t("members.ownerOnly")}</p>
        </Panel>
      ) : (
        <div className="space-y-4">
          <InviteForm
            workspaceId={workspaceId}
            onCreated={(link) => {
              setShownLink(link);
              void refresh();
            }}
          />
          {shownLink ? <InviteReveal link={shownLink} onDone={() => setShownLink(null)} /> : null}
        </div>
      )}

      {isMember ? null : (
        <section className="space-y-3">
          <h2 className="font-display text-xl font-extrabold tracking-[0.01em] text-ink uppercase">
            {t("members.roster")}
          </h2>
          <LabelGrid>
            {(members.data ?? []).map((member) => (
              <MemberRow
                key={member.operatorId}
                member={member}
                isOwner={member.operatorId === ownerId}
                isSelf={member.operatorId === me.data?.id}
                busy={busy}
                onRoleChange={(role) => changeRole.mutate({ operatorId: member.operatorId, role })}
                onRemove={() => setRemoving(member)}
              />
            ))}
          </LabelGrid>
        </section>
      )}

      {isMember ? null : (
        <section className="space-y-3">
          <h2 className="font-display text-xl font-extrabold tracking-[0.01em] text-ink uppercase">
            {t("members.pending")}
          </h2>
          {pending.length === 0 ? (
            <p className="text-note text-ink-2">{t("members.noPending")}</p>
          ) : (
            <LabelGrid>
              {pending.map((invite) => (
                <PendingInviteRow
                  key={invite.id}
                  invite={invite}
                  busy={busy}
                  onResend={() => resend.mutate(invite)}
                  onRevoke={() => setRevoking(invite)}
                />
              ))}
            </LabelGrid>
          )}
        </section>
      )}

      {removing ? (
        <ConfirmDialog
          title={t("common.remove")}
          message={t("members.confirmRemove", { name: removing.name || removing.email })}
          confirmLabel={t("common.remove")}
          variant="danger"
          busy={removeMember.isPending}
          error={removeMember.error}
          onCancel={() => setRemoving(null)}
          onConfirm={() => removeMember.mutate(removing.operatorId)}
        />
      ) : null}

      {revoking ? (
        <ConfirmDialog
          title={t("members.confirmRevokeTitle")}
          message={t("members.confirmRevoke", { email: revoking.email })}
          confirmLabel={t("members.revoke")}
          variant="danger"
          busy={revokeInvite.isPending}
          error={revokeInvite.error}
          onCancel={() => setRevoking(null)}
          onConfirm={() => revokeInvite.mutate(revoking.id)}
        />
      ) : null}
    </div>
  );
}
